// TODO (Copilot): This guide is the source of truth for variable syntax, scopes, recipes, and
// integration patterns. When an AI copilot is added to the workflow editor, it should load this
// content to answer questions like "how do I pass name to an email?", "create a NetSuite lead
// workflow", "undo the last node", etc. Recipes here should map to one-click workflow templates.

import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

type Tab = "overview" | "prechatSetup" | "prechat" | "askQuestion" | "setData" | "customFields" | "scopes" | "recipes" | "deployments";

function CodeBlock({ children }: { children: string }) {
  return (
    <code className="font-mono text-sm text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
      {children}
    </code>
  );
}

function ExampleCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">{title}</h4>
      {children}
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex justify-center py-2">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="text-blue-400"
      >
        <path
          d="M12 5v14m0 0l-5-5m5 5l5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function FlowStep({
  step,
  title,
  description,
  color = "blue"
}: {
  step: number;
  title: string;
  description: React.ReactNode;
  color?: "blue" | "green" | "amber" | "purple";
}) {
  const colors = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200"
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold">
          {step}
        </span>
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="text-sm leading-relaxed">{description}</div>
    </div>
  );
}

const SCOPE_DISPLAY: Record<string, { label: string; color: string }> = {
  "Built-in": { label: "Built-in", color: "bg-slate-100 text-slate-600" },
  Contact:    { label: "Permanent", color: "bg-blue-100 text-blue-700" },
  Conversation: { label: "This Chat", color: "bg-amber-100 text-amber-700" },
  Workflow:   { label: "Step Only", color: "bg-green-100 text-green-700" }
};

function ScopeTag({ scope }: { scope: string }) {
  const def = SCOPE_DISPLAY[scope] ?? { label: scope, color: "bg-slate-100 text-slate-600" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${def.color}`}>
      {def.label}
    </span>
  );
}

function NavItem({ id, label, active, onClick }: { id: Tab; label: string; active: boolean; onClick: (id: Tab) => void }) {
  return (
    <button
      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
        active
          ? "bg-primary-bg text-primary-active font-medium"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
      }`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );
}

function NavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none">
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <span className="text-green-600 font-medium">✓ Copied</span> : "Copy"}
    </button>
  );
}

function CodeSnippet({ label, code }: { label?: string; code: string }) {
  return (
    <div className="bg-slate-900 rounded-lg overflow-hidden">
      {label && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700">
          <span className="text-[11px] text-slate-400 font-medium">{label}</span>
          <CopyButton text={code} />
        </div>
      )}
      {!label && (
        <div className="flex justify-end px-3 pt-1.5">
          <CopyButton text={code} />
        </div>
      )}
      <pre className="text-xs text-slate-200 font-mono whitespace-pre-wrap px-3 pb-3 pt-1">{code}</pre>
    </div>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          What are variables?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Variables are like fill-in-the-blanks for your workflows. Instead of
          writing the same message for everyone, you can use a placeholder like{" "}
          <CodeBlock>{"<<name>>"}</CodeBlock> and the system fills in each
          visitor&apos;s actual name automatically.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Think of it like a mail merge in a word processor — you write the
          template once, and each person gets a personalized version.
        </p>
      </div>

      <ExampleCard title="Simple Example">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Visitor fills in prechat form"
            description={
              <span>
                Name: <strong>Sarah Johnson</strong> | Email:{" "}
                <strong>sarah@example.com</strong>
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='You write in "Send Message" node'
            description={
              <code className="text-sm font-mono">
                Hi {"<<name>>"}, thanks for contacting us!
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Visitor sees"
            description={
              <span className="font-medium">
                Hi Sarah Johnson, thanks for contacting us!
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Available Built-in Variables">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-4 font-medium text-slate-500">
                  Variable
                </th>
                <th className="text-left py-2 pr-4 font-medium text-slate-500">
                  What it contains
                </th>
                <th className="text-left py-2 font-medium text-slate-500">
                  Where it comes from
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<name>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Full name</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<firstName>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">First name only</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<lastName>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Last name only</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<email>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Email address</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<phone>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Phone number</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<language>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">
                  Browser language code
                </td>
                <td className="py-2 text-slate-500">
                  Detected from browser. Your embed script can pass a specific locale to override this (e.g. to match a multilingual site's active language).
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ExampleCard>

      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 text-sm">
        <h4 className="font-semibold text-slate-700 mb-2">How variable names are determined</h4>
        <p className="text-slate-600 leading-relaxed mb-2">
          The <strong>field label</strong> is what the visitor sees on the form (e.g. "Your name").
          The <strong>variable name</strong> — what you type in <CodeBlock>{"<<  >>"}</CodeBlock> — is set in
          the survey editor's <strong>Attribute</strong> field — that value becomes the variable name.
          Built-in fields (Name, Email, Phone) are automatically mapped to{" "}
          <CodeBlock>{"<<name>>"}</CodeBlock>, <CodeBlock>{"<<email>>"}</CodeBlock>,
          and <CodeBlock>{"<<phone>>"}</CodeBlock>. Custom questions create a variable with whatever name you put in the Attribute field.
        </p>
        <p className="text-xs text-slate-500">
          Convention: use camelCase for custom variable names —{" "}
          <CodeBlock>orderNumber</CodeBlock>, <CodeBlock>helpTopic</CodeBlock>,{" "}
          <CodeBlock>companySize</CodeBlock>. No spaces or special characters.
        </p>
      </div>
    </div>
  );
}

function ConfigStep({
  step,
  title,
  children
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold mt-0.5">
        {step}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
        {children}
      </div>
    </div>
  );
}

function NavHint({ to, label }: { to: string; label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-md px-2 py-1 text-xs text-slate-600">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        className="text-slate-400"
      >
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        Go to <strong>{to}</strong> &rarr; {label}
      </span>
    </div>
  );
}

function PrechatSetupTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Setting up prechat surveys
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          A prechat survey is the form visitors fill out before their chat
          starts. You deploy <strong>one script</strong> on your website and
          use <strong>Rules</strong> to control which survey shows up — the
          same script can show different forms based on conditions you set
          (page URL, time of day, language, country, etc.).
        </p>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 mt-3">
          <div className="text-sm text-blue-700">
            <strong>One script, one deploy.</strong> You paste one embed code on
            your website. Rules decide everything else — which survey to show,
            which chat window style to use, which workflow to run. No need to
            manage multiple scripts.
          </div>
        </div>
      </div>

      {/* Configuration Example 1: Default prechat */}
      <ExampleCard title="Example 1: One Default Prechat Survey for Everyone">
        <p className="text-sm text-slate-600 mb-4">
          The simplest setup — every visitor sees the same form before chatting.
          This is how most people start.
        </p>

        <div className="space-y-5">
          <ConfigStep step={1} title="Create your survey">
            <NavHint to="General Settings" label="Surveys" />
            <div className="mt-2 bg-slate-50 rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500 mb-2 font-medium">
                Click &quot;New Survey&quot;, name it &quot;Default
                Prechat&quot;, and add questions:
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    Name
                  </span>
                  <span className="text-slate-600">
                    &quot;Your name&quot; — Required
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    Email
                  </span>
                  <span className="text-slate-600">
                    &quot;Email address&quot; — Required
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">
                    Choice
                  </span>
                  <span className="text-slate-600">
                    &quot;What do you need help with?&quot; — Options: Billing,
                    Technical, General
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Hit Save when done.
              </p>
            </div>
          </ConfigStep>

          <ConfigStep step={2} title="Create a rule to use this survey">
            <NavHint to="General Settings" label="Deployment &rarr; Rules tab" />
            <div className="mt-2 bg-slate-50 rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500 mb-2">
                Click &quot;New Rule&quot; and configure:
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">Trigger:</span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                    Use Prechat Survey
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">
                    Conditions:
                  </span>
                  <span className="text-slate-600 italic">
                    None — leave empty so it applies to every visitor
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">Survey:</span>
                  <span className="text-slate-700 font-medium">
                    Select &quot;Default Prechat&quot;
                  </span>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={3} title="That's it! Variables are automatically available">
            <div className="mt-1 bg-green-50 rounded-lg border border-green-200 p-3">
              <p className="text-sm text-green-700">
                When a visitor fills out this form, the values automatically
                become variables in your workflow:
              </p>
              <div className="font-mono text-xs text-green-800 mt-2 space-y-0.5">
                <div>
                  {"<<name>>"} = whatever they typed in &quot;Your name&quot;
                </div>
                <div>
                  {"<<email>>"} = whatever they typed in &quot;Email
                  address&quot;
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Choice questions become variables too — set the variable name in the
                survey&apos;s <strong>Attribute</strong> field for that question.
              </p>
            </div>
          </ConfigStep>
        </div>
      </ExampleCard>

      {/* Configuration Example 2: Different surveys by condition */}
      <ExampleCard title="Example 2: Different Surveys Based on Conditions">
        <p className="text-sm text-slate-600 mb-3">
          Same single embed code on your website, but you show different
          prechat forms depending on the situation. Use rules with conditions
          to control which survey appears.
        </p>
        <p className="text-sm text-slate-600 mb-4">
          For example: visitors on your pricing page get asked about company
          size and budget. Visitors on your help page get asked for an order
          number.
        </p>

        <div className="space-y-5">
          <ConfigStep step={1} title="Create a survey for each scenario">
            <NavHint to="General Settings" label="Surveys" />
            <div className="mt-2 space-y-3">
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                <div className="text-xs font-semibold text-blue-700 mb-2">
                  Survey: &quot;Sales Prechat&quot;
                </div>
                <div className="space-y-1 text-sm text-blue-600">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>
                    Q3: Company name (text, variable: &quot;company&quot;)
                  </div>
                  <div>
                    Q4: Company size (choice: 1-10 / 11-50 / 50+, variable:
                    &quot;companySize&quot;)
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <div className="text-xs font-semibold text-amber-700 mb-2">
                  Survey: &quot;Support Prechat&quot;
                </div>
                <div className="space-y-1 text-sm text-amber-600">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>
                    Q3: Order number (text, variable: &quot;orderNumber&quot;)
                  </div>
                  <div>
                    Q4: Issue type (choice: Billing / Technical / Shipping,
                    variable: &quot;issueType&quot;)
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold text-slate-600 mb-2">
                  Survey: &quot;Default Prechat&quot; (fallback for everyone
                  else)
                </div>
                <div className="space-y-1 text-sm text-slate-500">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>Q3: How can we help? (text)</div>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={2} title="Create rules with conditions">
            <NavHint to="General Settings" label="Deployment &rarr; Rules tab" />
            <p className="text-xs text-slate-500 mt-2 mb-3">
              Create multiple &quot;Use Prechat Survey&quot; rules. Rules are
              checked in priority order — the first one whose conditions match
              wins. Put your most specific rules first (priority 1, 2) and
              your default last.
            </p>
            <div className="mt-2 space-y-3">
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 1
                  </span>
                  <span className="text-xs font-semibold text-blue-700">
                    Sales pages get the Sales survey
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-blue-600">
                  <div>
                    Trigger:{" "}
                    <strong className="text-blue-800">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="bg-white rounded px-1.5 py-0.5 border border-blue-200 text-xs font-mono">
                      URL contains &quot;/pricing&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-blue-800">
                      &quot;Sales Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 2
                  </span>
                  <span className="text-xs font-semibold text-amber-700">
                    Help pages get the Support survey
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-amber-600">
                  <div>
                    Trigger:{" "}
                    <strong className="text-amber-800">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="bg-white rounded px-1.5 py-0.5 border border-amber-200 text-xs font-mono">
                      URL contains &quot;/help&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-amber-800">
                      &quot;Support Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 3
                  </span>
                  <span className="text-xs font-semibold text-slate-600">
                    Everyone else gets the default
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-slate-500">
                  <div>
                    Trigger:{" "}
                    <strong className="text-slate-700">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="italic">
                      None — no conditions means &quot;always matches&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-slate-700">
                      &quot;Default Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={3} title="Variables work the same regardless of which survey showed">
            <div className="mt-1 bg-green-50 rounded-lg border border-green-200 p-3">
              <p className="text-sm text-green-700 mb-2">
                No matter which survey the visitor got, the built-in variables
                are always the same:
              </p>
              <div className="font-mono text-xs text-green-800 space-y-0.5">
                <div>
                  {"<<name>>"} and {"<<email>>"} — always available from any
                  survey
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Custom variables (like{" "}
                <CodeBlock>{"<<orderNumber>>"}</CodeBlock> or{" "}
                <CodeBlock>{"<<company>>"}</CodeBlock>) are only available if
                the visitor&apos;s survey had that question. Use{" "}
                <code className="bg-green-200 px-1 rounded font-mono">
                  {"?."}
                </code>{" "}
                in your workflow or check with a Condition node first.
              </p>
            </div>
          </ConfigStep>
        </div>
      </ExampleCard>

      {/* Available conditions */}
      <ExampleCard title="What Conditions Can You Use?">
        <p className="text-sm text-slate-600 mb-3">
          When creating a rule, you can add conditions to control when it
          applies. Here are all the conditions you can use:
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            {
              name: "URL",
              desc: "Page the visitor is on (contains /pricing, /help, etc.)"
            },
            {
              name: "Domain",
              desc: "Which website (support.acme.com vs www.acme.com)"
            },
            {
              name: "Country",
              desc: "Visitor's country (US, UK, CA, etc.)"
            },
            {
              name: "Language",
              desc: "Browser language (English, Spanish, French)"
            },
            {
              name: "Day of Week",
              desc: "Monday through Sunday"
            },
            {
              name: "Time of Day",
              desc: "Business hours vs after hours"
            },
            {
              name: "Channel",
              desc: "Web, SMS, WhatsApp, Facebook, etc."
            },
            {
              name: "Attribute",
              desc: "Any variable saved to Contact (permanent) scope — e.g. tier, language, accountId"
            },
            {
              name: "Team",
              desc: "Which team is assigned"
            },
            {
              name: "Team Availability",
              desc: "Are agents online or offline?"
            }
          ].map((item) => (
            <div
              key={item.name}
              className="bg-slate-50 rounded border border-slate-200 p-2"
            >
              <div className="text-xs font-semibold text-slate-700">
                {item.name}
              </div>
              <div className="text-[11px] text-slate-500">{item.desc}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">
          You can combine multiple conditions with AND/OR logic. For example:
          &quot;URL contains /pricing AND country is US&quot;.
        </p>
      </ExampleCard>

      {/* How it all connects */}
      <ExampleCard title="How It All Connects">
        <p className="text-sm text-slate-600 mb-4">
          Here&apos;s the complete picture — one embed code, rules decide
          everything.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="You paste one embed script on your website"
            description={
              <span className="text-xs">
                Go to General Settings &rarr; Deployment &rarr; Deployment Code
                tab. Copy the script, paste it before{" "}
                <code className="bg-white/50 px-1 rounded">{"</body>"}</code> on
                every page where you want chat.
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="When a visitor arrives, rules are checked in priority order"
            description={
              <span className="text-xs">
                The system goes through your rules from priority 1, 2, 3... The
                first &quot;Use Prechat Survey&quot; rule whose conditions match
                determines which form to show.
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Visitor fills out the prechat form"
            description={
              <span className="text-xs">
                Their answers are saved as variables:{" "}
                <code className="bg-white/50 px-1 rounded">{"<<name>>"}</code>,{" "}
                <code className="bg-white/50 px-1 rounded">{"<<email>>"}</code>,
                plus any custom variables you configured on the survey questions.
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Chat starts, your workflow has all the data"
            description={
              <span className="text-xs">
                All prechat variables are available immediately. Use them in Send
                Message, HTTP Request, Email, Set Data — anywhere you see the{" "}
                <code className="bg-white/50 px-1 rounded font-mono">
                  {"<< >>"}
                </code>{" "}
                button.
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      {/* Tips card */}
      <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
        <h4 className="text-sm font-semibold text-amber-800 mb-2">
          Tips for prechat surveys
        </h4>
        <ul className="text-sm text-amber-700 space-y-2 list-disc list-inside">
          <li>
            <strong>Keep it short</strong> — 2-4 questions max. Every extra
            question reduces the chance a visitor will start a chat.
          </li>
          <li>
            <strong>Always require name and email</strong> — these become{" "}
            <CodeBlock>{"<<name>>"}</CodeBlock> and{" "}
            <CodeBlock>{"<<email>>"}</CodeBlock>, which are the most useful
            variables for personalization.
          </li>
          <li>
            <strong>Use Choice questions for routing</strong> — if you ask
            &quot;What department?&quot; as a choice, you can use the answer in
            a Condition node to route to different workflow paths.
          </li>
          <li>
            <strong>Name your variables on questions</strong> — in the survey editor,
            the <strong>Attribute</strong> field sets the variable name for that question&apos;s answer.
            Set it to something like &quot;orderNumber&quot; so you can use{" "}
            <CodeBlock>{"<<orderNumber>>"}</CodeBlock> in your workflow.
          </li>
          <li>
            <strong>A rule with no conditions is your default</strong> — put
            it last (lowest priority). It catches everyone not matched by a
            more specific rule above it.
          </li>
          <li>
            <strong>Priority order matters</strong> — rules are checked from
            priority 1 down. Put your most specific rules first, your default
            last.
          </li>
        </ul>
      </div>
    </div>
  );
}

function PrechatTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          How prechat form data flows into your workflow
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          When a visitor fills out the prechat form before starting a chat, their
          answers are automatically saved as variables you can use anywhere in
          your workflow. You don&apos;t need to do anything special — it just works.
        </p>
      </div>

      <ExampleCard title="Complete Prechat Flow Example">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Visitor sees prechat form on your website"
            description={
              <div className="bg-white rounded border border-slate-300 p-3 mt-2 max-w-xs">
                <div className="text-xs text-slate-500 mb-2 font-medium">
                  Before we start, tell us about yourself:
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Name</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      Maria Garcia
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Email</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      maria@shopify-store.com
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Phone</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      (555) 123-4567
                    </div>
                  </div>
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="System automatically saves these variables"
            description={
              <div className="space-y-1 mt-1 font-mono text-xs">
                <div>
                  {"<<name>>"} = &quot;Maria Garcia&quot;
                </div>
                <div>
                  {"<<firstName>>"} = &quot;Maria&quot;
                </div>
                <div>
                  {"<<lastName>>"} = &quot;Garcia&quot;
                </div>
                <div>
                  {"<<email>>"} = &quot;maria@shopify-store.com&quot;
                </div>
                <div>
                  {"<<phone>>"} = &quot;(555) 123-4567&quot;
                </div>
              </div>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Your Send Message node uses them"
            description={
              <div className="mt-1">
                <div className="text-xs text-slate-500 mb-1">You type:</div>
                <code className="text-xs font-mono block bg-white/50 p-2 rounded">
                  Welcome {"<<firstName>>"}! I see you&apos;re reaching out from{" "}
                  {"<<email>>"}. How can I help?
                </code>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Maria sees the personalized message"
            description={
              <div className="mt-1 bg-white/50 p-2 rounded text-sm font-medium">
                Welcome Maria! I see you&apos;re reaching out from
                maria@shopify-store.com. How can I help?
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="What if the visitor skips a field?">
        <p className="text-sm text-slate-600 mb-3">
          If a visitor doesn&apos;t fill in a field (or your prechat form doesn&apos;t
          ask for it), the variable will be empty. Your message will still work —
          the placeholder will just be replaced with nothing.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="text-xs font-medium text-amber-700 mb-1">
            Tip: Use Ask Question first
          </div>
          <p className="text-xs text-amber-600">
            If you need a piece of information and aren&apos;t sure the prechat
            collected it, add an &quot;Ask Question&quot; node early in your workflow.
            Save the answer as a variable, and you can use it later.
          </p>
        </div>
      </ExampleCard>

      <ExampleCard title="Prechat + HTTP Request Example">
        <p className="text-sm text-slate-600 mb-3">
          A common pattern is using prechat data to look up a customer in your
          CRM or support system.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat collects email"
            description={
              <span>
                <CodeBlock>{"<<email>>"}</CodeBlock> ={" "}
                <strong>maria@shopify-store.com</strong>
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="HTTP Request looks up the customer"
            description={
              <code className="text-xs font-mono">
                GET https://api.yourcrm.com/lookup?email={"<<email>>"}
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send a personalized response"
            description={
              <code className="text-xs font-mono">
                Hi {"<<firstName>>"}, I found your account. How can I help?
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function AskQuestionTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Collecting answers with Ask Question
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          The &quot;Ask Question&quot; node lets you ask the visitor something and
          save their answer as a variable. You pick the variable name, and the
          visitor&apos;s response is stored there for use in later nodes.
        </p>
      </div>

      <ExampleCard title="Step-by-Step: Collecting an Order Number">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title='Configure your "Ask Question" node'
            description={
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Question:</span>
                  <span className="text-sm">
                    &quot;What is your order number?&quot;
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Type:</span>
                  <span className="text-sm">Text</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Save as:</span>
                  <span className="text-sm font-mono bg-slate-100 px-1 rounded">
                    orderNumber
                  </span>
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Visitor types their answer"
            description={
              <span>
                Visitor says: <strong>&quot;ORD-98765&quot;</strong>
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="System saves the answer"
            description={
              <span className="font-mono text-sm">
                {"<<orderNumber>>"} = &quot;ORD-98765&quot;
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Use it in a later Send Message"
            description={
              <div>
                <div className="text-xs text-slate-500 mb-1">Template:</div>
                <code className="text-xs font-mono">
                  Looking up order {"<<orderNumber>>"} for you now...
                </code>
                <div className="text-xs text-slate-500 mt-2 mb-1">
                  Visitor sees:
                </div>
                <span className="text-sm font-medium">
                  Looking up order ORD-98765 for you now...
                </span>
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Multiple Choice Example">
        <p className="text-sm text-slate-600 mb-3">
          Multiple choice questions let the visitor pick from options you
          define. Each option can lead to a different workflow path.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Configure the question"
            description={
              <div className="mt-1 space-y-2">
                <div>
                  <span className="text-xs text-slate-500">Question: </span>
                  <span className="text-sm">
                    &quot;What do you need help with?&quot;
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Type: </span>
                  <span className="text-sm">Multiple Choice</span>
                </div>
                <div className="text-xs text-slate-500">Options:</div>
                <div className="ml-2 space-y-1 text-sm">
                  <div className="bg-white/50 border rounded px-2 py-1">
                    Billing question
                  </div>
                  <div className="bg-white/50 border rounded px-2 py-1">
                    Technical support
                  </div>
                  <div className="bg-white/50 border rounded px-2 py-1">
                    General inquiry
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Save as: </span>
                  <span className="font-mono text-sm bg-slate-100 px-1 rounded">
                    helpTopic
                  </span>
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Visitor clicks "Billing question"'
            description={
              <span className="font-mono text-sm">
                {"<<helpTopic>>"} = &quot;Billing question&quot;
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function SetDataTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Storing data with Set Data
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          The &quot;Set Data&quot; node lets you create or update variables without
          asking the visitor anything. It&apos;s useful for storing fixed values,
          combining existing variables, or preparing data for an API call.
        </p>
      </div>

      <ExampleCard title="Common Uses">
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Store a fixed value
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>department</strong>
              </div>
              <div>
                Value: <strong>billing</strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Now you can use <CodeBlock>{"<<department>>"}</CodeBlock> anywhere
              = &quot;billing&quot;
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Build a message from variables
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>summary</strong>
              </div>
              <div>
                Value:{" "}
                <strong>
                  {"<<firstName>>"} needs help with {"<<helpTopic>>"}
                </strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              <CodeBlock>{"<<summary>>"}</CodeBlock> ={" "}
              &quot;Maria needs help with Billing question&quot;
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Tag the conversation for agents
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>priority</strong>
              </div>
              <div>
                Value: <strong>high</strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Save as <ScopeTag scope="Conversation" /> so agents can see it in
              the chat sidebar
            </div>
          </div>
        </div>
      </ExampleCard>

      <ExampleCard title="Real-World: Prepare Data for a CRM API">
        <p className="text-sm text-slate-600 mb-3">
          Use Set Data to organize information before sending it to an external
          system.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat and Ask Question collect data"
            description={
              <div className="font-mono text-xs space-y-0.5 mt-1">
                <div>{"<<name>>"} = &quot;Maria Garcia&quot;</div>
                <div>{"<<email>>"} = &quot;maria@shopify-store.com&quot;</div>
                <div>{"<<orderNumber>>"} = &quot;ORD-98765&quot;</div>
                <div>{"<<helpTopic>>"} = &quot;Billing question&quot;</div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Set Data builds a summary"
            description={
              <div className="font-mono text-xs space-y-0.5 mt-1">
                <div>
                  Name: <strong>ticketTitle</strong>
                </div>
                <div>
                  Value:{" "}
                  <strong>
                    {"<<helpTopic>>"} - Order {"<<orderNumber>>"}
                  </strong>
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="HTTP Request sends it to your CRM"
            description={
              <code className="text-xs font-mono block mt-1">
                {`POST https://api.yourcrm.com/tickets`}
                <br />
                {`{ "title": "<<ticketTitle>>", "email": "<<email>>" }`}
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function TryItStep({ step, title, detail, code }: { step: number; title: string; detail: string; code?: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold mt-0.5">{step}</div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-700 mb-0.5">{title}</div>
        <div className="text-xs text-slate-500 mb-1.5 leading-relaxed">{detail}</div>
        {code && (
          <div className="bg-slate-900 rounded-md overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700">
              <span className="text-[10px] text-slate-400 font-mono">{code}</span>
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:text-slate-200 transition-colors ml-3 shrink-0"
                onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              >
                {copied ? <span className="text-green-400">✓ Copied</span> : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CustomFieldsTab() {
  return (
    <div className="space-y-6">

      {/* The one rule — biggest, first */}
      <div className="bg-blue-600 rounded-xl p-5 text-white">
        <div className="text-lg font-bold mb-2">The one rule you need to know</div>
        <div className="text-blue-100 text-sm mb-4 leading-relaxed">
          Every variable — whether it's a built-in field like email or a field you created yourself — is used
          exactly the same way: wrap the name in double angle brackets.
        </div>
        <div className="bg-white/10 rounded-lg p-3 font-mono text-sm">
          <span className="text-blue-200">{"Hi "}</span>
          <span className="text-white font-bold">{"<<firstName>>"}</span>
          <span className="text-blue-200">{", your plan is "}</span>
          <span className="text-white font-bold">{"<<plan>>"}</span>
          <span className="text-blue-200">{". How can I help?"}</span>
        </div>
        <div className="mt-3 text-blue-100 text-xs">
          <code className="bg-white/10 px-1.5 py-0.5 rounded">firstName</code> = built-in.
          &nbsp;<code className="bg-white/10 px-1.5 py-0.5 rounded">plan</code> = a field you added.
          &nbsp;Same syntax for both. No difference.
        </div>
      </div>

      {/* Two types of fields */}
      <ExampleCard title="Two types of fields — both work the same way">
        <div className="grid gap-3 sm:grid-cols-2">

          {/* Built-in */}
          <div className="rounded-lg border-2 border-slate-200 overflow-hidden">
            <div className="bg-slate-700 text-white px-3 py-2 text-xs font-semibold">
              Built-in fields
            </div>
            <div className="p-3">
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Velaro knows these automatically — no setup needed.
                They come from the prechat form, contact record, or browser.
              </p>
              <div className="space-y-1.5">
                {[
                  ["<<name>>", "Full name"],
                  ["<<firstName>>", "First name only"],
                  ["<<email>>", "Email address"],
                  ["<<phone>>", "Phone number"],
                  ["<<language>>", "Browser language"],
                ].map(([v, d]) => (
                  <div key={v} className="flex items-center gap-2 text-xs">
                    <code className="font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">{v}</code>
                    <span className="text-slate-500">{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Your fields */}
          <div className="rounded-lg border-2 border-blue-300 overflow-hidden">
            <div className="bg-blue-600 text-white px-3 py-2 text-xs font-semibold">
              Fields you add (in Manage Fields)
            </div>
            <div className="p-3">
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Fields you create in <strong>Contact Center → Manage Fields</strong>.
                You name them whatever you want.
              </p>
              <div className="space-y-1.5">
                {[
                  ["<<plan>>", "If you created a field named 'plan'"],
                  ["<<account_tier>>", "If you named it 'account_tier'"],
                  ["<<company>>", "If you named it 'company'"],
                  ["<<loyalty_points>>", "If you named it 'loyalty_points'"],
                  ["<<region>>", "If you named it 'region'"],
                ].map(([v, d]) => (
                  <div key={v} className="flex items-center gap-2 text-xs">
                    <code className="font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">{v}</code>
                    <span className="text-slate-500">{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <strong>The field name is the variable name.</strong> When you create a field and name it <code className="bg-amber-100 px-1 rounded font-mono">plan</code>,
          you use <code className="bg-amber-100 px-1 rounded font-mono">{"<<plan>>"}</code> everywhere. That's it.
        </div>
      </ExampleCard>

      {/* How a field gets a value */}
      <ExampleCard title="How does a field get a value?">
        <p className="text-sm text-slate-600 mb-4">
          You define the field once in Manage Fields (just the name and type).
          Then a <em>value</em> gets stored on each contact when something happens during a chat.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="You create the field once"
            description={
              <div className="text-xs mt-1">
                Go to <strong>Contact Center → Manage Fields → Add Field</strong>.
                Name it <code className="bg-white/50 px-1 rounded font-mono">plan</code>, type = Text, scope = Contact (permanent).
                This is just the definition — no data yet.
              </div>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="A value gets stored when a visitor goes through a workflow"
            description={
              <div className="text-xs mt-1 space-y-1">
                <div>Any of these stores the value on the contact's record:</div>
                <div className="font-mono mt-1.5 space-y-0.5 text-[11px]">
                  <div>→ <strong>Set Data node</strong>: name = <code>plan</code>, value = <code>"Pro"</code>, scope = Contact</div>
                  <div>→ <strong>Ask Question node</strong>: "Save reply as" = <code>plan</code>, toggle Contact on</div>
                  <div>→ <strong>Your website embed</strong>: pass it via <code>{"customData: { plan: 'Pro' }"}</code></div>
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Next time they chat — just use <<plan>> anywhere"
            description={
              <div className="text-xs mt-1">
                <code className="bg-white/50 px-1 rounded font-mono">{"Hi <<firstName>>, I see you're on the <<plan>> plan. How can I help?"}</code>
                <div className="mt-2 font-semibold">→ Maria sees: Hi Maria, I see you're on the Pro plan. How can I help?</div>
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      {/* Try it */}
      <ExampleCard title="Try it — working test in 4 steps">
        <p className="text-sm text-slate-600 mb-4">
          Follow these steps and you'll see a variable working end-to-end in under 5 minutes.
        </p>
        <div className="space-y-4">
          <TryItStep
            step={1}
            title="Create the field"
            detail="Contact Center → Manage Fields → Add Field. Label = 'Plan', Variable name = plan, Type = Text, Scope = Contact. Save."
          />
          <TryItStep
            step={2}
            title="Create a test workflow"
            detail="Workflows → New Workflow. Name it 'Variable Test'."
          />
          <TryItStep
            step={3}
            title="Add a Set Data node, then a Send Message node"
            detail="Set Data: name = plan, value = Pro, scope = Contact. Then Send Message: copy the text below."
            code={"Hi <<firstName>>, you're on the <<plan>> plan. Welcome!"}
          />
          <TryItStep
            step={4}
            title="Test it"
            detail="Publish and start a test chat. You should see 'Hi [your name], you're on the Pro plan. Welcome!' — the variable was set in step 3 and read back in the message."
          />
        </div>
        <div className="mt-4 bg-green-50 rounded-lg border border-green-200 p-3 text-xs text-green-800">
          <strong>What just happened:</strong> Set Data stored "Pro" as the value of <code className="bg-green-100 px-1 rounded font-mono">plan</code> on the contact.
          The message read it back using <code className="bg-green-100 px-1 rounded font-mono">{"<<plan>>"}</code>.
          Same syntax — whether built-in or a field you created.
        </div>
      </ExampleCard>

      {/* Real-world examples */}
      <ExampleCard title="Real-world examples">
        <div className="space-y-4">

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">Route VIP customers to a priority queue</div>
            <div className="p-3 space-y-2 text-xs">
              <div className="text-slate-400 mb-2">Field: <code className="font-mono">plan</code> (Contact) · values: VIP, Pro, Starter</div>
              <div className="flex flex-col gap-1.5 font-mono">
                <div><span className="text-slate-400">Send Message: </span><span className="text-blue-700">{"Hi <<firstName>>, I see you're on the <<plan>> plan."}</span></div>
                <div><span className="text-slate-400">Condition: </span><span className="text-amber-700">{"plan equals 'VIP'"}</span></div>
                <div><span className="text-slate-400">→ Yes: </span><span className="text-green-700">Transfer to VIP Queue</span></div>
                <div><span className="text-slate-400">→ No: </span><span className="text-slate-600">Continue standard flow</span></div>
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">Skip asking for email if you already have it</div>
            <div className="p-3 space-y-2 text-xs">
              <div className="text-slate-400 mb-2">Built-in field: <code className="font-mono">email</code> — already stored from a previous chat</div>
              <div className="flex flex-col gap-1.5 font-mono">
                <div><span className="text-slate-400">Condition: </span><span className="text-amber-700">{"email exists"}</span></div>
                <div><span className="text-slate-400">→ Yes: </span><span className="text-green-700">{"Send: 'Welcome back, <<firstName>>! How can I help?'"}</span></div>
                <div><span className="text-slate-400">→ No: </span><span className="text-slate-600">Ask Question: "What's your email?" → save as email</span></div>
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">Give an AI bot context about who it's talking to</div>
            <div className="p-3 text-xs">
              <div className="text-slate-400 mb-2">Use fields in the AI's system prompt so it can personalize responses</div>
              <CodeSnippet code={"You are a support agent for Acme Corp.\nThe customer's name is <<firstName>> <<lastName>>.\nThey are on the <<plan>> plan. Their company is <<company>>.\nAlways address them by first name and be aware of their plan when answering."} />
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">Send contact data to your CRM or webhook</div>
            <div className="p-3 text-xs">
              <div className="text-slate-400 mb-2">HTTP Request node body — all fields work in the URL, headers, and body</div>
              <CodeSnippet code={'{\n  "email": "<<email>>",\n  "name": "<<name>>",\n  "plan": "<<plan>>",\n  "company": "<<company>>"\n}'} />
            </div>
          </div>

        </div>
      </ExampleCard>

      {/* Being specific — scope paths */}
      <ExampleCard title="Being specific — where does the system look?">
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          When you type <CodeBlock>{"<<plan>>"}</CodeBlock>, the system searches three stores in order and returns the
          first match it finds. Most of the time this just works. But if you want to <strong>guarantee</strong> which
          store you're reading from, use the explicit path.
        </p>

        {/* Why contact.custom and not just contact.plan */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">Why is it <code className="font-mono bg-white border border-slate-200 px-1 rounded">contact.custom.plan</code> and not just <code className="font-mono bg-white border border-slate-200 px-1 rounded">contact.plan</code>?</div>
          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            Because <code className="bg-white border border-slate-200 px-1 rounded font-mono">contact.*</code> is already used for built-in fields Velaro always knows.
            Without a separator, the system can't tell if <code className="bg-white border border-slate-200 px-1 rounded font-mono">contact.plan</code> is a built-in it should know about or a field you created.
          </p>
          <div className="rounded border border-slate-200 overflow-hidden text-xs font-mono">
            <div className="bg-slate-100 px-3 py-1.5 text-[10px] font-sans font-semibold text-slate-500 uppercase tracking-wide">The split</div>
            <div className="divide-y divide-slate-100">
              <div className="px-3 py-2 flex items-start gap-3">
                <span className="text-slate-400 shrink-0 w-28">contact.email</span>
                <span className="text-slate-400 shrink-0">→</span>
                <span className="text-slate-600">Built-in. Velaro always knows this. Set from prechat form.</span>
              </div>
              <div className="px-3 py-2 flex items-start gap-3">
                <span className="text-slate-400 shrink-0 w-28">contact.firstName</span>
                <span className="text-slate-400 shrink-0">→</span>
                <span className="text-slate-600">Built-in. Always available.</span>
              </div>
              <div className="px-3 py-2 flex items-start gap-3 bg-blue-50">
                <span className="text-blue-700 shrink-0 w-28">contact.custom.plan</span>
                <span className="text-slate-400 shrink-0">→</span>
                <span className="text-blue-700"><strong>You created this</strong> in Manage Fields. The <code className="bg-blue-100 px-0.5 rounded">custom</code> namespace means "fields I added myself."</span>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            <strong>When would you actually write it this way?</strong> When the same name exists in two places — see the example below.
          </p>
        </div>

        {/* Resolution order visual */}
        <div className="rounded-lg border border-slate-200 overflow-hidden mb-4">
          <div className="bg-slate-700 text-white text-xs font-semibold px-3 py-2">
            Resolution order — first match wins
          </div>
          <div className="divide-y divide-slate-100">
            {[
              {
                rank: "1",
                scope: "Workflow",
                color: "bg-green-100 text-green-700",
                path: "<<workflow.plan>>",
                desc: "Temporary — exists only while this workflow is running. Cleared when the workflow finishes.",
                example: "Set Data node with scope = Workflow"
              },
              {
                rank: "2",
                scope: "Conversation",
                color: "bg-amber-100 text-amber-700",
                path: "<<conversation.plan>>",
                desc: "This chat only — shared across all workflows in the same chat, gone when the chat closes.",
                example: "Set Data node with scope = Conversation"
              },
              {
                rank: "3",
                scope: "Contact (your fields)",
                color: "bg-blue-100 text-blue-700",
                path: "<<contact.custom.plan>>",
                desc: "Permanent — stored on the contact record, available in every future chat.",
                example: "Set Data node with scope = Contact, or the embed script"
              },
              {
                rank: "4",
                scope: "Contact (built-in)",
                color: "bg-slate-100 text-slate-600",
                path: "<<contact.email>>",
                desc: "Always available — name, email, phone, language. Set automatically from prechat form.",
                example: "Prechat form, contact record"
              },
            ].map(({ rank, scope, color, path, desc, example }) => (
              <div key={rank} className="flex items-start gap-3 p-3 hover:bg-slate-50">
                <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{rank}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${color}`}>{scope}</span>
                    <code className="font-mono text-xs text-blue-700">{path}</code>
                  </div>
                  <div className="text-xs text-slate-500 leading-snug">{desc}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">How it gets set: {example}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* When ambiguity matters */}
        <div className="space-y-3">
          <div className="text-xs font-semibold text-slate-600 mb-1">When does this actually matter?</div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <div className="font-semibold text-amber-800 mb-1.5">Scenario — same name in two stores</div>
            <p className="text-amber-700 mb-2 leading-relaxed">
              Your contact field is <code className="bg-amber-100 px-1 rounded font-mono">plan = "Pro"</code> (permanent).
              But earlier in this workflow you set a temporary override: <code className="bg-amber-100 px-1 rounded font-mono">plan = "Trial"</code> (conversation scope).
            </p>
            <div className="space-y-1 font-mono">
              <div className="flex items-center gap-2">
                <code className="text-amber-800">{"<<plan>>"}</code>
                <span className="text-amber-600">→ returns <strong>"Trial"</strong> (conversation wins — it's ranked higher)</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-amber-800">{"<<contact.custom.plan>>"}</code>
                <span className="text-amber-600">→ returns <strong>"Pro"</strong> (pinned to contact record)</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-amber-800">{"<<conversation.plan>>"}</code>
                <span className="text-amber-600">→ returns <strong>"Trial"</strong> (pinned to this chat)</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
            <div className="font-semibold text-slate-700 mb-1.5">Full path reference</div>
            <div className="space-y-1 font-mono">
              {[
                ["<<contact.custom.plan>>", "Field you created in Manage Fields, permanent on contact"],
                ["<<contact.email>>", "Built-in contact field (email, name, phone, etc.)"],
                ["<<conversation.ticket_id>>", "Set during this chat, gone when chat closes"],
                ["<<workflow.tempFlag>>", "Temporary scratch — gone when workflow finishes"],
              ].map(([path, desc]) => (
                <div key={path} className="flex items-start gap-2">
                  <code className="text-blue-700 shrink-0">{path}</code>
                  <span className="text-slate-400 font-sans">— {desc}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-400 leading-relaxed">
            <strong className="text-slate-500">Bottom line:</strong> Use the short form <CodeBlock>{"<<plan>>"}</CodeBlock> for everyday use.
            Use the explicit path when you have two variables with the same name at different scopes and need to be precise about which one you're reading.
          </p>
        </div>
      </ExampleCard>

    </div>
  );
}

function ScopesTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          How long does saved data last?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          When you save a variable (using Ask Question or Set Data), you choose
          how long to keep it. There are three options — pick the shortest one
          that still gets the job done.
        </p>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
          <span className="font-semibold">Quick decision guide:</span>
          <ul className="mt-2 space-y-1">
            <li>→ Will they need this info remembered weeks from now? → <span className="font-medium">Permanent</span></li>
            <li>→ Only needed while they&apos;re chatting today? → <span className="font-medium">This Chat</span></li>
            <li>→ Just a temporary calculation inside this one workflow? → <span className="font-medium">Step Only</span></li>
          </ul>
        </div>
      </div>

      <div className="grid gap-4">
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Contact" />
            <span className="text-sm font-semibold text-blue-800">
              Remembered forever — even in future chats
            </span>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            Like a customer profile. This data sticks with the person across all
            their chats, even months later.
          </p>
          <div className="bg-white rounded border border-blue-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Email address, phone number, full name</li>
              <li>Customer tier (VIP, standard, enterprise)</li>
              <li>Language preference</li>
              <li>Account ID or membership number</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-blue-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              Maria chats today and gives her email. Two weeks later she chats
              again — <CodeBlock>{"<<email>>"}</CodeBlock> still has her email.
              No need to ask again.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Conversation" />
            <span className="text-sm font-semibold text-amber-800">
              Gone when this chat ends
            </span>
          </div>
          <p className="text-sm text-amber-700 mb-3">
            Like a sticky note for this conversation. All workflows in this chat
            can see it, but it&apos;s gone when the chat ends.
          </p>
          <div className="bg-white rounded border border-amber-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Order number they&apos;re asking about</li>
              <li>What topic they need help with</li>
              <li>Ticket number created during the chat</li>
              <li>Issue category or department</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-amber-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              Maria says her order number is ORD-98765. You save it as{" "}
              <CodeBlock>{"<<orderNumber>>"}</CodeBlock> (This Chat).
              If the chat gets transferred to another workflow, that workflow can
              still see the order number — but once the chat closes, it&apos;s gone.
            </p>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Workflow" />
            <span className="text-sm font-semibold text-green-800">
              Gone the moment this workflow finishes
            </span>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Like scratch paper. Only this specific workflow can see it, and
            it&apos;s thrown away when the workflow finishes.
          </p>
          <div className="bg-white rounded border border-green-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Temporary calculations or counters</li>
              <li>API response data you only need briefly</li>
              <li>Internal flags (&quot;hasVerifiedEmail&quot;, &quot;retryCount&quot;)</li>
              <li>Intermediate steps in multi-part processes</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-green-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              You call an API to check if a coupon code is valid. Save the
              result as <CodeBlock>{"<<couponValid>>"}</CodeBlock> (Step Only).
              You only need it for the next Condition node — it disappears the
              moment this workflow finishes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipesTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Ready-to-use workflow recipes
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          Ready-to-use workflow patterns. Each recipe shows the full flow from start to finish.
          Code blocks have a <strong>Copy</strong> button — paste them directly into your HTTP Request nodes or system prompts.
        </p>
      </div>

      <ExampleCard title="Recipe: Customer Support Intake">
        <p className="text-sm text-slate-600 mb-3">
          Greet the visitor, find out what they need, and prepare the info for
          your agent team.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Send Message: Greet by name"
            description={
              <code className="text-xs font-mono">
                Hi {"<<firstName>>"}, welcome to our support chat! I&apos;m here to
                help.
              </code>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Ask Question: "What do you need help with?"'
            description={
              <div className="text-xs space-y-0.5 mt-1">
                <div>Type: Multiple Choice</div>
                <div>Options: Billing | Technical | Account | Other</div>
                <div>
                  Save as:{" "}
                  <span className="font-mono bg-white/50 px-1 rounded">
                    helpTopic
                  </span>{" "}
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title='Ask Question: "Can you describe the issue?"'
            description={
              <div className="text-xs space-y-0.5 mt-1">
                <div>Type: Text</div>
                <div>
                  Save as:{" "}
                  <span className="font-mono bg-white/50 px-1 rounded">
                    issueDescription
                  </span>{" "}
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Set Data: Build agent summary"
            description={
              <div className="font-mono text-xs mt-1 space-y-0.5">
                <div>
                  Name: <strong>agentNote</strong>
                </div>
                <div>
                  Value:{" "}
                  <strong>
                    {"<<name>>"} ({"<<email>>"}) — {"<<helpTopic>>"}:{" "}
                    {"<<issueDescription>>"}
                  </strong>
                </div>
                <div className="mt-1">
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={5}
            title="Send Message: tell the visitor what happens next"
            description={
              <code className="text-xs font-mono">
                Got it, {"<<firstName>>"}! Connecting you to an agent now — they can see everything you&apos;ve shared. Typical wait is 2–3 minutes.
              </code>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={6}
            title="Transfer to Agents"
            description={
              <span className="text-xs">
                Agent sees the full conversation history + <code className="font-mono">agentNote</code> variable in their contact panel:{" "}
                <span className="font-medium">
                  &quot;Maria Garcia (maria@shopify-store.com) — Billing: I was
                  charged twice for my last order&quot;
                </span>
                <span className="block text-slate-400 italic mt-1">Workflow ends here. The agent takes over.</span>
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Recipe: Order Lookup with API">
        <p className="text-sm text-slate-600 mb-3">
          Ask for an order number, look it up in your system, and give the
          visitor a status update.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Ask Question: Get the order number"
            description={
              <div className="text-xs mt-1">
                Question: &quot;What&apos;s your order number? (e.g. ORD-12345)&quot;
                <br />
                Save as:{" "}
                <span className="font-mono bg-white/50 px-1 rounded">
                  orderId
                </span>{" "}
                <ScopeTag scope="Conversation" />
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="HTTP Request: Look up the order"
            description={
              <code className="text-xs font-mono block mt-1">
                GET https://api.yourstore.com/orders/{"<<orderId>>"}
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send Message: Share the status"
            description={
              <code className="text-xs font-mono block mt-1">
                Your order {"<<orderId>>"} is currently: {"<<orderStatus>>"}.
                Expected delivery: {"<<deliveryDate>>"}.
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Recipe: Email Follow-Up">
        <p className="text-sm text-slate-600 mb-3">
          Collect the issue, then send a confirmation email to the visitor with
          all the details.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat provides email and name"
            description={
              <div className="font-mono text-xs mt-1">
                {"<<email>>"} = maria@shopify-store.com | {"<<name>>"} = Maria
                Garcia
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Ask Question: "Describe your issue"'
            description={
              <div className="text-xs mt-1">
                Save as:{" "}
                <span className="font-mono bg-white/50 px-1 rounded">
                  issueDetails
                </span>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send Email node"
            description={
              <div className="text-xs mt-1 space-y-1">
                <div>
                  To: <code className="font-mono">{"<<email>>"}</code>
                </div>
                <div>
                  Subject:{" "}
                  <code className="font-mono">
                    We received your request, {"<<firstName>>"}
                  </code>
                </div>
                <div>
                  Body:{" "}
                  <code className="font-mono">
                    Hi {"<<firstName>>"}, we&apos;ve received your message about:{" "}
                    {"<<issueDetails>>"}. A team member will get back to you
                    within 24 hours.
                  </code>
                </div>
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      {/* ─── NetSuite ─── */}
      <ExampleCard title="Recipe: NetSuite Lead Creation (Read + Write)">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-800">
          <strong>Why NetSuite needs more steps than other CRMs:</strong> NetSuite uses two
          separate APIs — <strong>SuiteQL</strong> (read/search) and the <strong>REST Record API</strong> (write).
          You can't query "Leads" as a table — leads are Customers with <code className="font-mono bg-white/50 px-1 rounded">entitystatus = 6</code>.
          Custom fields use prefixed IDs: <code className="font-mono bg-white/50 px-1 rounded">custentity_</code> for contact-level
          fields, <code className="font-mono bg-white/50 px-1 rounded">custbody_</code> for transaction body fields.
        </div>
        <div className="space-y-1">
          <FlowStep step={1} title="Prechat collects contact info" description={
            <div className="font-mono text-xs mt-1 space-y-0.5">
              <div>{"<<name>>"} = "Jordan Smith" | {"<<email>>"} = "jordan@acme.com"</div>
              <div>{"<<company>>"} = "Acme Corp" (custom variable from survey)</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="HTTP GET — Check if contact exists in NS (SuiteQL)" description={
            <div className="mt-1 space-y-2">
              <CodeSnippet label="GET — SuiteQL search" code={`GET https://YOUR_ACCOUNT.suitetalk.api.netsuite.com/services/rest/query/v1/suiteql
?q=SELECT id, entitystatus FROM customer WHERE email = '<<email>>' LIMIT 1

Headers:
  Authorization: Bearer <<nsToken>>
  Prefer: transient`} />
              <p className="text-xs text-slate-500">Save response field <code className="font-mono">id</code> as <code className="font-mono">nsContactId</code> (Conversation scope). If empty, proceed to step 3 to create.</p>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="HTTP POST — Create lead record" description={
            <div className="mt-1 space-y-2">
              <CodeSnippet label="POST — Create customer as Lead" code={`POST https://YOUR_ACCOUNT.suitetalk.api.netsuite.com/services/rest/record/v1/customer

{
  "companyname": "<<company>>",
  "email": "<<email>>",
  "entitystatus": { "id": "6" },
  "custentity_chat_source": "velaro",
  "custentity_chat_issue": "<<helpTopic>>",
  "custentity_lead_name": "<<name>>"
}

Headers:
  Authorization: Bearer <<nsToken>>
  Content-Type: application/json`} />
              <p className="text-xs text-slate-500">NS returns the new record ID in the <code className="font-mono">Location</code> response header. Extract it and save as <code className="font-mono">nsLeadId</code>.</p>
            </div>
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="Confirm to visitor" description={
            <code className="text-xs font-mono">Thanks {"<<name>>"} — we've created a lead record and a member of our team will follow up at {"<<email>>"}.</code>
          } color="green" />
        </div>
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600 space-y-1">
          <div><strong>NS custom field naming:</strong></div>
          <div><code className="font-mono">custentity_*</code> — entity-level fields on Customer/Contact records</div>
          <div><code className="font-mono">custbody_*</code> — transaction-level fields (invoices, sales orders)</div>
          <div><code className="font-mono">custrecord_*</code> — fields on custom record types</div>
          <div className="pt-1">Find your field IDs in NS under <strong>Customization → Lists, Records &amp; Fields → Entity Fields</strong>.</div>
        </div>
      </ExampleCard>

      {/* ─── Language Routing ─── */}
      <ExampleCard title="Recipe: Language-Based Routing">
        <p className="text-sm text-slate-600 mb-3">
          Use the visitor's browser language to greet them in their own language and route to the right team.
          The <CodeBlock>{"<<language>>"}</CodeBlock> variable returns a BCP 47 code like <code className="font-mono text-xs">en-US</code>, <code className="font-mono text-xs">es-MX</code>, <code className="font-mono text-xs">fr-FR</code>.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Condition node: branch by language" description={
            <div className="mt-1 space-y-1 text-xs">
              <div className="bg-blue-50 border border-blue-200 rounded p-2">Condition: <code className="font-mono">{"<<language>>"}</code> starts with <code className="font-mono">es</code> → Spanish path</div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2">Condition: <code className="font-mono">{"<<language>>"}</code> starts with <code className="font-mono">fr</code> → French path</div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2">Default → English path</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Send Message in matching language" description={
            <div className="mt-1 space-y-1 text-xs">
              <div>🇪🇸 <code className="font-mono">Hola {"<<firstName>>"}, ¿en qué puedo ayudarte hoy?</code></div>
              <div>🇫🇷 <code className="font-mono">Bonjour {"<<firstName>>"}, comment puis-je vous aider?</code></div>
              <div>🌐 <code className="font-mono">Hi {"<<firstName>>"}, how can I help you today?</code></div>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Route to language-specific team" description={
            <span className="text-xs">Use Transfer node pointing to Spanish Support / French Support / General Support team based on the same branch.</span>
          } color="green" />
        </div>
        <div className="mt-3 text-xs text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
          Tip: If your site serves localized pages with different HTML lang attributes (e.g. <code className="font-mono">{"<html lang=\"de\">"}</code>), you can guarantee Velaro picks up the right language by passing it explicitly in your embed script:{" "}
          <CodeSnippet code={`velaro('set', 'language', document.documentElement.lang || navigator.language);`} />
        </div>
      </ExampleCard>

      {/* ─── VIP Detection ─── */}
      <ExampleCard title="Recipe: VIP Customer Fast-Track">
        <p className="text-sm text-slate-600 mb-3">
          Look up the visitor in your CRM, detect their tier, and route VIP customers directly to your top agents.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Prechat collects email" description={<CodeBlock>{"<<email>>"}</CodeBlock>} color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="HTTP GET — fetch customer tier" description={
            <CodeSnippet label="GET customer record" code={`GET https://api.yourcrm.com/customers?email=<<email>>

Response field to extract: tier
Save as: customerTier  (Conversation scope)`} />
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Condition: route by tier" description={
            <div className="text-xs space-y-1 mt-1">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2">If <code className="font-mono">{"<<customerTier>>"}</code> equals <code className="font-mono">VIP</code> → Set Data: <code className="font-mono">priority = urgent</code> → Transfer to VIP Queue</div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2">Else → standard intake flow</div>
            </div>
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="Send VIP greeting" description={
            <code className="text-xs font-mono">Hi {"<<firstName>>"}, as a valued VIP member you're being connected to a senior specialist right now.</code>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── After Hours ─── */}
      <ExampleCard title="Recipe: After-Hours Auto-Response">
        <p className="text-sm text-slate-600 mb-3">
          When visitors reach out outside business hours, acknowledge them instantly, create a follow-up ticket, and set proper expectations.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Condition: check team availability or time window" description={
            <div className="text-xs mt-1 space-y-1">
              <div className="bg-slate-50 border border-slate-200 rounded p-2">Condition: Team Availability is Offline (or: Hour of Day is not between 9am–5pm)</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Send Message: acknowledge and explain" description={
            <code className="text-xs font-mono">Hi {"<<firstName>>"}, thanks for reaching out! Our team is currently offline. We're available Monday–Friday 9am–5pm ET. Leave a message and we'll reply first thing.</code>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Ask Question: collect their issue" description={
            <div className="text-xs mt-1">Question: "Briefly describe what you need help with"<br />Save as: <code className="font-mono">afterHoursMessage</code> (Conversation)</div>
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="HTTP POST — create internal ticket + confirm" description={
            <div className="space-y-2 mt-1">
              <CodeSnippet label="POST ticket" code={`POST https://api.yourcrm.com/tickets
{
  "subject": "After-hours chat from <<name>>",
  "email": "<<email>>",
  "message": "<<afterHoursMessage>>",
  "source": "chat-after-hours",
  "priority": "normal"
}`} />
              <code className="text-xs font-mono block mt-1">Confirm: Thanks! We've noted your message and will email {"<<email>>"} as soon as we're back online.</code>
            </div>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Callback Request ─── */}
      <ExampleCard title="Recipe: Appointment / Callback Request">
        <p className="text-sm text-slate-600 mb-3">
          Let visitors book a callback without waiting. Collect their availability, confirm instantly, and push to your scheduling system.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title='Ask Question: "What is the best number to reach you?"' description={
            <div className="text-xs mt-1">Type: Text | Save as: <code className="font-mono">callbackPhone</code> (Contact scope — keeps it for next time)</div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title='Ask Question: preferred callback window' description={
            <div className="text-xs mt-1">
              Type: Multiple Choice<br />
              Options: Morning (9–12) | Afternoon (12–5) | Evening (5–7)<br />
              Save as: <code className="font-mono">callbackWindow</code> (Conversation)
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="HTTP POST — add to scheduling queue" description={
            <CodeSnippet label="POST callback request" code={`POST https://api.yourcrm.com/callbacks
{
  "name": "<<name>>",
  "email": "<<email>>",
  "phone": "<<callbackPhone>>",
  "preferred_window": "<<callbackWindow>>",
  "source": "chat"
}`} />
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="Confirm with summary" description={
            <code className="text-xs font-mono">Got it, {"<<firstName>>"}! We'll call {"<<callbackPhone>>"} during {"<<callbackWindow>>"}. You'll also get a confirmation at {"<<email>>"}.</code>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Account Verification ─── */}
      <ExampleCard title="Recipe: Account Verification Gate">
        <p className="text-sm text-slate-600 mb-3">
          Verify identity before showing sensitive data or routing to a specialist.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title='Ask Question: "What is your account or member number?"' description={
            <div className="text-xs mt-1">Type: Text | Save as: <code className="font-mono">accountId</code> (Conversation)</div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="HTTP GET — validate account" description={
            <CodeSnippet label="GET account validation" code={`GET https://api.yourplatform.com/accounts/<<accountId>>/validate?email=<<email>>

Response: { "valid": true, "name": "Jordan Smith", "tier": "premium" }
Save: accountValid, accountTier`} />
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Condition: branch on result" description={
            <div className="text-xs space-y-1 mt-1">
              <div className="bg-green-50 border border-green-200 rounded p-2">If <code className="font-mono">{"<<accountValid>>"}</code> = true → personalized flow with account data</div>
              <div className="bg-red-50 border border-red-200 rounded p-2">Else → "I couldn't find that account number. Can you double-check?" → Ask again or transfer to agent</div>
            </div>
          } color="purple" />
        </div>
      </ExampleCard>

      {/* ─── Velaro Ticket ─── */}
      <ExampleCard title="Recipe: Create a Support Ticket on Transfer">
        <p className="text-sm text-slate-600 mb-3">
          When a bot hands off to a human agent, create a ticket record automatically so nothing slips through.
          The bot collects context, creates the ticket, then transfers — the agent opens the chat and the ticket is already there.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 text-xs text-blue-800">
          <strong>How Transfer works:</strong> The Transfer node ends the workflow and moves the chat into the agent queue.
          The agent sees the full conversation history. Any variables saved to <strong>Conversation</strong> scope appear
          in the agent&apos;s contact panel. <strong>Always add a Send Message node before Transfer</strong> — otherwise
          the visitor sees nothing while waiting.
        </div>
        <div className="space-y-1">
          <FlowStep step={1} title="Collect issue type and description" description={
            <div className="font-mono text-xs space-y-0.5 mt-1">
              <div>{"<<issueType>>"} — Multiple Choice: Billing | Technical | Account | Other</div>
              <div>{"<<issueDescription>>"} — Text: "Briefly describe your issue"</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="HTTP POST — create internal ticket" description={
            <CodeSnippet label="POST ticket (your internal API or Velaro webhook)" code={`POST https://api.yourcompany.com/tickets
Content-Type: application/json

{
  "subject": "<<issueType>> — <<name>>",
  "contact_email": "<<email>>",
  "contact_name": "<<name>>",
  "description": "<<issueDescription>>",
  "source": "chat",
  "channel": "<<channel>>"
}

Extract from response: id → save as ticketNumber (Conversation)`} />
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title='Send Message: acknowledge + set expectation (REQUIRED before Transfer)' description={
            <code className="text-xs font-mono">
              Thanks {"<<firstName>>"} — I&apos;ve created ticket #{"{"}{"<<ticketNumber>>"}{"}"} for your {"<<issueType>>"} request.
              Connecting you to an agent now. Typical wait is 2–3 minutes.
            </code>
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="Transfer to Agents" description={
            <div className="text-xs mt-1 space-y-1">
              <div>Agent sees: full conversation + <code className="font-mono">{"<<ticketNumber>>"}</code>, <code className="font-mono">{"<<issueType>>"}</code>, <code className="font-mono">{"<<issueDescription>>"}</code> in the contact panel</div>
              <div className="text-slate-400 italic">Workflow ends here — the agent takes over.</div>
            </div>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Product Routing ─── */}
      <ExampleCard title="Recipe: Product Specialist Routing">
        <p className="text-sm text-slate-600 mb-3">
          Route visitors to a team that specializes in the product they're asking about, and give that team context immediately.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title='Ask Question: "Which product are you asking about?"' description={
            <div className="text-xs mt-1">
              Type: Multiple Choice<br />
              Options: Product A | Product B | Product C | Not sure<br />
              Save as: <code className="font-mono">productInterest</code> (Conversation)
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Set Data: build agent context note" description={
            <div className="font-mono text-xs mt-1 space-y-0.5">
              <div>Name: <strong>agentContext</strong></div>
              <div>Value: <strong>{"<<name>>"} is asking about {"<<productInterest>>"} — {"<<issueDescription>>"}</strong></div>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Condition: branch to specialist team" description={
            <div className="text-xs space-y-1 mt-1">
              <div>If <code className="font-mono">{"<<productInterest>>"}</code> = "Product A" → Transfer to Product-A Team</div>
              <div>If <code className="font-mono">{"<<productInterest>>"}</code> = "Product B" → Transfer to Product-B Team</div>
              <div>Default → General Support team</div>
            </div>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Proactive Entry Point ─── */}
      <ExampleCard title="Recipe: Proactive Chat with Page Context">
        <p className="text-sm text-slate-600 mb-3">
          Use the page the visitor is on to personalize the opening message and pass context to the agent — no questions asked.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Set Data: record entry point from URL variable" description={
            <div className="font-mono text-xs mt-1 space-y-0.5">
              <div>Name: <strong>entryPage</strong></div>
              <div>Value: <strong>{"<<pageUrl>>"}</strong> (built-in, contains current page URL)</div>
              <div className="mt-1">Name: <strong>entryChannel</strong></div>
              <div>Value: <strong>{"<<channel>>"}</strong> (web / SMS / WhatsApp etc.)</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Condition: branch by page" description={
            <div className="text-xs space-y-1 mt-1">
              <div>URL contains <code className="font-mono">/pricing</code> → sales greeting</div>
              <div>URL contains <code className="font-mono">/checkout</code> → checkout help greeting</div>
              <div>URL contains <code className="font-mono">/support</code> → support greeting</div>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Send targeted opening message" description={
            <div className="text-xs space-y-1 mt-1">
              <div>Pricing: <code className="font-mono">Hi! Considering our plans? I can walk you through pricing and answer any questions.</code></div>
              <div>Checkout: <code className="font-mono">Hi! Need help completing your order? I'm here.</code></div>
            </div>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Multilingual Team ─── */}
      <ExampleCard title="Recipe: Multilingual Greeting + Team Routing">
        <p className="text-sm text-slate-600 mb-3">
          Combine language detection with team routing so Spanish visitors get Spanish-speaking agents without any setup from the visitor.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Set Data: normalize language to region" description={
            <div className="font-mono text-xs mt-1 space-y-0.5">
              <div>{"<<language>>"} = "es-MX" → normalize to "es" using Condition + Set Data</div>
              <div>Name: <strong>lang</strong> | Value: first 2 chars of <strong>{"<<language>>"}</strong></div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Condition: branch by lang" description={
            <div className="text-xs space-y-1 mt-1">
              <div><code className="font-mono">{"<<lang>>"}</code> = es → Spanish greeting + Transfer to Spanish Team</div>
              <div><code className="font-mono">{"<<lang>>"}</code> = fr → French greeting + Transfer to French Team</div>
              <div><code className="font-mono">{"<<lang>>"}</code> = de → German greeting + Transfer to DACH Team</div>
              <div>Default → English greeting + General Team</div>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Send greeting in matched language + transfer" description={
            <code className="text-xs font-mono">Hola {"<<firstName>>"}, te estoy conectando con nuestro equipo de habla hispana.</code>
          } color="green" />
        </div>
      </ExampleCard>

      {/* ─── Collect + Validate + Sync ─── */}
      <ExampleCard title="Recipe: Collect, Validate & Sync to CRM">
        <p className="text-sm text-slate-600 mb-3">
          A complete data-capture loop: collect what you need, confirm it's correct with the visitor, then push to your CRM in one shot.
        </p>
        <div className="space-y-1">
          <FlowStep step={1} title="Ask Questions: collect key fields" description={
            <div className="text-xs mt-1 space-y-0.5">
              <div>Q1: "What's your company name?" → Save as <code className="font-mono">company</code> (Contact)</div>
              <div>Q2: "How many employees?" → Save as <code className="font-mono">companySize</code> (Contact)</div>
              <div>Q3: "What's your main challenge?" → Save as <code className="font-mono">painPoint</code> (Conversation)</div>
            </div>
          } color="blue" />
          <FlowArrow />
          <FlowStep step={2} title="Set Data: build a confirmation summary" description={
            <div className="font-mono text-xs mt-1 space-y-0.5">
              <div>Name: <strong>summary</strong></div>
              <div>Value: <strong>{"<<name>>"} @ {"<<company>>"} ({"<<companySize>>"} people) — {"<<painPoint>>"}</strong></div>
            </div>
          } color="amber" />
          <FlowArrow />
          <FlowStep step={3} title="Ask Question: confirm before sending" description={
            <div className="text-xs mt-1">
              Send: <code className="font-mono">Just to confirm — {"<<summary>>"} — is that right? (Yes / Let me correct it)</code><br />
              Save answer as <code className="font-mono">confirmed</code>
            </div>
          } color="purple" />
          <FlowArrow />
          <FlowStep step={4} title="HTTP POST: sync to CRM (only if confirmed)" description={
            <CodeSnippet label="POST to CRM" code={`POST https://api.yourcrm.com/leads
{
  "name": "<<name>>",
  "email": "<<email>>",
  "company": "<<company>>",
  "company_size": "<<companySize>>",
  "pain_point": "<<painPoint>>",
  "source": "chat"
}`} />
          } color="green" />
        </div>
      </ExampleCard>

    </div>
  );
}

function DeploymentsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          What is a Deployment?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          A deployment is a separate chat entry point for your website or app.
          Each deployment gets its own embed code, default team routing, and chat
          window appearance. Think of deployments as different &quot;doors&quot;
          visitors can enter through.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          All deployments share the same account, users, workflows, and rules.
          The difference is each one can be placed on a different page, brand,
          or department — each with its own look and routing.
        </p>
      </div>

      <ExampleCard title="Deployments vs Teams">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="text-sm font-semibold text-blue-800 mb-2">
              Deployments
            </div>
            <p className="text-sm text-blue-700 mb-3">
              Separate chat entry points — different embed codes for different
              sites, brands, or departments.
            </p>
            <ul className="text-sm text-blue-600 space-y-1 list-disc list-inside">
              <li>Each gets its own embed script</li>
              <li>Different chat window appearance</li>
              <li>Different default team routing</li>
            </ul>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <div className="text-sm font-semibold text-amber-800 mb-2">
              Teams
            </div>
            <p className="text-sm text-amber-700 mb-3">
              Groups of agents within a single chat experience — used for
              routing and availability.
            </p>
            <ul className="text-sm text-amber-600 space-y-1 list-disc list-inside">
              <li>Route chats to specific agent groups</li>
              <li>Set availability per team</li>
              <li>Transfer between teams mid-chat</li>
              <li>Same embed code for all teams</li>
            </ul>
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 mt-4">
          <div className="text-xs font-medium text-slate-500 mb-1">
            Rule of thumb
          </div>
          <p className="text-sm text-slate-600">
            Use <strong>teams</strong> when you want different groups of agents
            handling chats from the <em>same</em> chat window. Use{" "}
            <strong>deployments</strong> when you need completely separate chat
            experiences (different websites, brands, or app sections).
          </p>
        </div>
      </ExampleCard>

      <ExampleCard title="When to Use Multiple Deployments">
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Multiple brands or websites
            </div>
            <p className="text-xs text-slate-500">
              Your company runs support.acme.com and shop.acme.com — each gets
              its own deployment with a different chat window style and default
              team.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Department-specific chat
            </div>
            <p className="text-xs text-slate-500">
              Your sales page has one chat widget (routed to Sales team) and your
              help center has another (routed to Support team). Different look,
              different greeting, different team.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Embedded in different apps
            </div>
            <p className="text-xs text-slate-500">
              You embed chat in your main website, your mobile app webview, and
              your partner portal. Each gets its own deployment so you can track
              and configure them independently.
            </p>
          </div>
        </div>
      </ExampleCard>

      <ExampleCard title="How It Works">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Create a deployment in Settings"
            description={
              <span className="text-xs">
                Go to General Settings &rarr; Deployment. Give it a name, pick a
                default team, and save.
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Copy the embed script"
            description={
              <span className="text-xs">
                Each deployment has its own unique embed code. Go to the
                &quot;Deployment Code&quot; tab to copy it.
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Paste it on the target page"
            description={
              <span className="text-xs">
                Add the script before{" "}
                <code className="bg-white/50 px-1 rounded">{"</body>"}</code> on
                the page where you want this chat to appear.
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="All changes happen in the admin — no redeployment needed"
            description={
              <span className="text-xs">
                Change the team, chat window, or rules at any time in the admin
                UI. The embed script stays the same — your website doesn&apos;t need
                to be updated.
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <div className="bg-green-50 rounded-lg border border-green-200 p-4">
        <h4 className="text-sm font-semibold text-green-800 mb-2">
          Key points
        </h4>
        <ul className="text-sm text-green-700 space-y-2 list-disc list-inside">
          <li>
            <strong>One site ID, multiple embed scripts</strong> — all
            deployments share the same account and data.
          </li>
          <li>
            <strong>No redeployment needed</strong> — once the embed script is
            placed, all behavior changes (team, chat window, rules) happen in
            the admin UI.
          </li>
          <li>
            <strong>Shared workflows and rules</strong> — your workflows work
            across all deployments.
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function VariableGuide() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as Tab) ?? "overview";
  const setActiveTab = (tab: Tab) => setSearchParams({ tab }, { replace: true });
  const navigate = useNavigate();

  return (
    <div className="h-full flex overflow-hidden">
      {/* ── Left sidebar nav ── */}
      <div className="w-52 shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="px-3 pt-3 pb-2 border-b border-slate-100">
          <button
            onClick={() => navigate("/workflows")}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors w-full"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0">
              <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Workflows
          </button>
        </div>
        <div className="p-3 space-y-5">
          <NavSection title="Learn">
            <NavItem id="overview" label="How It Works" active={activeTab === "overview"} onClick={setActiveTab} />
          </NavSection>

          <NavSection title="Collecting Data">
            <NavItem id="prechatSetup" label="Prechat Setup" active={activeTab === "prechatSetup"} onClick={setActiveTab} />
            <NavItem id="prechat" label="Prechat Data" active={activeTab === "prechat"} onClick={setActiveTab} />
            <NavItem id="askQuestion" label="Ask Question" active={activeTab === "askQuestion"} onClick={setActiveTab} />
            <NavItem id="setData" label="Set Data" active={activeTab === "setData"} onClick={setActiveTab} />
          </NavSection>

          <NavSection title="Reference">
            <NavItem id="customFields" label="Custom Fields" active={activeTab === "customFields"} onClick={setActiveTab} />
            <NavItem id="scopes" label="Scopes" active={activeTab === "scopes"} onClick={setActiveTab} />
            <NavItem id="recipes" label="Recipes" active={activeTab === "recipes"} onClick={setActiveTab} />
          </NavSection>

          <NavSection title="Deploy">
            <NavItem id="deployments" label="Deployments" active={activeTab === "deployments"} onClick={setActiveTab} />
          </NavSection>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="p-6 max-w-3xl">
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "prechatSetup" && <PrechatSetupTab />}
          {activeTab === "prechat" && <PrechatTab />}
          {activeTab === "askQuestion" && <AskQuestionTab />}
          {activeTab === "setData" && <SetDataTab />}
          {activeTab === "customFields" && <CustomFieldsTab />}
          {activeTab === "scopes" && <ScopesTab />}
          {activeTab === "recipes" && <RecipesTab />}
          {activeTab === "deployments" && <DeploymentsTab />}
        </div>
      </div>
    </div>
  );
}
